import {
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { InlineLoading } from "../../../common/components/Loading";
import { useActionRequest } from "../../../common/hooks/api";
import { OrganizationUser } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import { Error as RequestsError } from "../../copy/requests";
import { Fmt } from "../../formatters";
import { useOrganizationUsers, usePrimaryOrganizationId, useSelf } from "../../hooks";
import { usePrimaryOrganization, useSelfOrganizationUser } from "../../hooks/useOrganization";
import { Add as AddIcon } from "@mui/icons-material";
import { ConfirmDialog, InviteUserDialog } from "../dialog";
import { formatUserRole } from "../../util/formatters";
import { SelectMenuDropdown } from "@truevault/aquilo";
import { ChildOrganization } from "../../../common/service/server/controller/OrganizationVendorClientController";
import { useSnackBar } from "@truevault/aquilo";

const useChildOrgs = (orgId: string) => {
  const [childOrgs, setChildOrgs] = useState<ChildOrganization[]>([]);
  const [childOrgUsers, setChildOrgUsers] = useState<Record<string, OrganizationUser[]>>({});

  useEffect(() => {
    const loadChildOrgs = async () => {
      try {
        const vendorClients = await Api.organizationVendorClient.getChildOrganizations(orgId);
        // Sort the organizations alphabetically by name
        const sortedOrgs = vendorClients.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChildOrgs(sortedOrgs);

        const usersMap: Record<string, OrganizationUser[]> = {};
        for (const childOrg of sortedOrgs) {
          const response = await Api.organizationUser.getUsers(childOrg.publicId);
          usersMap[childOrg.publicId] = response;
        }
        setChildOrgUsers(usersMap);
      } catch (error) {
        console.error("Failed to load child organizations:", error);
      }
    };

    loadChildOrgs();
  }, [orgId]); // Only re-run if orgId changes

  return { childOrgs, childOrgUsers, setChildOrgUsers };
};

const UsersTable = ({ users, parentOrgUsers, onNeedsRefresh, allowAddingHrUser, org }) => {
  const [self] = useSelf();
  const selfOrgUser = useSelfOrganizationUser();
  const { childOrgs, childOrgUsers, setChildOrgUsers } = useChildOrgs(org.id);
  const [isRoleSelectorOpen, setIsRoleSelectorOpen] = useState(false);
  const enqueueSnackbar = useSnackBar();

  const [removeUserConfirmOpen, setRemoveUserConfirmOpen] = useState(false);
  const [selectedOrgForRemoval, setSelectedOrgForRemoval] = useState<ChildOrganization | null>(
    null,
  );
  const [userToRemove, setUserToRemove] = useState<OrganizationUser | null>(null);

  const handleRemoveFromOrg = async (userToRemove: OrganizationUser) => {
    if (!selectedOrgForRemoval) return;

    try {
      await Api.organizationUser.deleteUser(selectedOrgForRemoval.publicId, userToRemove.email);

      // Update local state after successful removal
      setChildOrgUsers({
        ...childOrgUsers,
        [selectedOrgForRemoval.publicId]:
          childOrgUsers[selectedOrgForRemoval.publicId]?.filter(
            (u) => u.email !== userToRemove.email,
          ) || [],
      });

      enqueueSnackbar(`Removed ${userToRemove.email} from ${selectedOrgForRemoval.name}`, {
        variant: "success",
      });
      onNeedsRefresh();
    } catch (error) {
      console.error("Failed to remove user from organization:", error);
      enqueueSnackbar(`Failed to remove user: ${error.message}`, {
        variant: "error",
      });
    } finally {
      setRemoveUserConfirmOpen(false);
      setSelectedOrgForRemoval(null);
      setUserToRemove(null);
    }
  };

  return (
    <>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "30%" }}>Authorized User</TableCell>
            <TableCell style={{ width: "10%" }}>Access</TableCell>
            <TableCell style={{ width: "15%" }} align="right">
              Last Login
            </TableCell>
            <TableCell style={{ width: "20%" }} align="right">
              Actions
            </TableCell>
            <TableCell style={{ width: "30%" }} align="right">
              Organization Access
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.email}>
              <TableCell component="th" scope="row">
                <div className={"user-name-col"}>{`${user.firstName} ${user.lastName}`}</div>
                <div className={"user-email-col"}>{user.email}</div>
              </TableCell>
              <TableCell>
                {self?.email === user.email ||
                user.status != "ACTIVE" ||
                selfOrgUser?.role !== "ADMIN" ? (
                  formatUserRole(user.role)
                ) : (
                  <Select
                    variant={"standard"}
                    disableUnderline={true}
                    value={user.role}
                    onChange={async (event) => {
                      await Api.organizationUser.assignUserRole(
                        user.organization,
                        user.email,
                        event.target.value,
                      );
                      onNeedsRefresh();
                    }}
                    onOpen={() => setIsRoleSelectorOpen(true)}
                    onClose={() => setIsRoleSelectorOpen(false)}
                  >
                    <MenuItem value={"ADMIN"}>
                      {isRoleSelectorOpen ? (
                        <Tooltip title={"Full access"}>
                          <span>Admin</span>
                        </Tooltip>
                      ) : (
                        "Admin"
                      )}
                    </MenuItem>
                    <MenuItem value={"GENERAL"}>
                      {isRoleSelectorOpen ? (
                        <Tooltip title={"Partial access - no HR"}>
                          <span>General</span>
                        </Tooltip>
                      ) : (
                        "General"
                      )}
                    </MenuItem>
                    {allowAddingHrUser && (
                      <MenuItem value={"HR"}>
                        {isRoleSelectorOpen ? (
                          <Tooltip title={"HR access only"}>
                            <span>HR</span>
                          </Tooltip>
                        ) : (
                          "HR"
                        )}
                      </MenuItem>
                    )}
                  </Select>
                )}
              </TableCell>
              <TableCell align="right">
                {user.status != "INVITED" ? Fmt.Date.fmtDate(user.lastLogin) : <i>Pending</i>}{" "}
              </TableCell>
              <TableCell align="right">
                {selfOrgUser?.role === "ADMIN" && (
                  <UserActions user={user} onNeedsRefresh={onNeedsRefresh} />
                )}
              </TableCell>
              <TableCell align="right">
                <div className="tw-boundary">
                  {childOrgs.length > 0 ? (
                    <SelectMenuDropdown<string>
                      items={childOrgs.map((org) => ({
                        value: org.publicId,
                        label: org.name || "Unnamed Organization",
                        isSelected:
                          childOrgUsers[org.publicId]?.some(
                            (u) => u.email === user.email && u.status !== "INACTIVE",
                          ) || false,
                      }))}
                      isLocked={selfOrgUser?.role !== "ADMIN" || user?.role === "ADMIN"}
                      onSelect={async (orgId) => {
                        const selectedOrg = childOrgs.find((org) => org.publicId === orgId);
                        if (!selectedOrg) return;

                        // Check if user is already in this org
                        const isUserInOrg = childOrgUsers[orgId]?.some(
                          (u) => u.email === user.email && u.status !== "INACTIVE",
                        );

                        if (isUserInOrg) {
                          setUserToRemove(user);
                          setSelectedOrgForRemoval(selectedOrg);
                          setRemoveUserConfirmOpen(true);
                        } else {
                          // Invite user to org
                          try {
                            await Api.organizationUser.inviteUser(selectedOrg.publicId, {
                              email: user.email,
                              firstName: user.firstName,
                              lastName: user.lastName,
                              role: user.role,
                            });

                            // Update local state after successful invite
                            const updatedUsers = [...(childOrgUsers[selectedOrg.publicId] || [])];
                            updatedUsers.push({
                              email: user.email,
                              firstName: user.firstName,
                              lastName: user.lastName,
                              role: user.role,
                              status: "ACTIVE",
                              organization: selectedOrg.publicId,
                              organizationName: selectedOrg.name,
                            });
                            setChildOrgUsers({
                              ...childOrgUsers,
                              [selectedOrg.publicId]: updatedUsers,
                            });

                            enqueueSnackbar(`Invited ${user.email} to ${selectedOrg.name}`, {
                              variant: "success",
                            });
                            onNeedsRefresh();
                          } catch (error) {
                            console.error("Failed to invite user to organization:", error);
                            enqueueSnackbar(
                              `Failed to invite user to organization: ${error.message}`,
                              {
                                variant: "error",
                              },
                            );
                          }
                        }
                      }}
                      allowMultipleSelected={true}
                      selectedOverflowItemName="child orgs"
                      dropdownWidthPx={350}
                    />
                  ) : (
                    <span style={{ color: "#666" }}>No child organizations</span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {parentOrgUsers.map((parentOrgUser) => (
            <TableRow
              key={parentOrgUser.email}
              sx={{
                "& td, & th": {
                  color: "#918CA1 !important",
                  fontStyle: "italic !important",
                },
              }}
            >
              <TableCell component="th" scope="row">
                <div
                  className={"user-name-col"}
                >{`${parentOrgUser.firstName} ${parentOrgUser.lastName}`}</div>
                <div className={"user-email-col"}>{parentOrgUser.email}</div>
              </TableCell>
              <TableCell>Through {parentOrgUser.organizationName}</TableCell>
              <TableCell align="right">{Fmt.Date.fmtDate(parentOrgUser.lastLogin)}</TableCell>
              <TableCell align="right" />
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmDialog
        open={removeUserConfirmOpen}
        onClose={() => {
          setRemoveUserConfirmOpen(false);
          setSelectedOrgForRemoval(null);
          setUserToRemove(null);
        }}
        onConfirm={() => userToRemove && handleRemoveFromOrg(userToRemove)}
        confirmTitle={`Remove ${userToRemove?.email} from ${selectedOrgForRemoval?.name}?`}
        contentText={`Are you sure you want to remove ${userToRemove?.email} from ${selectedOrgForRemoval?.name}? They will no longer have access to this organization.`}
        confirmText="Remove Access"
      />
    </>
  );
};

const getUserName = (user: OrganizationUser): string => {
  if (user.firstName?.length > 0) {
    if (user.lastName?.length > 0) {
      return `${user.firstName} ${user.lastName}`;
    } else {
      return user.firstName;
    }
  } else {
    return user.email;
  }
};

const UserActions = ({
  user,
  onNeedsRefresh,
}: {
  user: OrganizationUser;
  onNeedsRefresh: () => void;
}) => {
  const [self] = useSelf();
  const { fetch: deleteUser, request: deleteRequest } = useActionRequest({
    api: () => Api.organizationUser.deleteUser(user.organization, user.email),
    messages: { defaultError: RequestsError.DeleteUser },
    onSuccess: onNeedsRefresh,
  });
  const { fetch: resendInvite } = useActionRequest({
    api: () =>
      Api.organizationUser.inviteUser(user.organization, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
      }),
    messages: {
      success: "Invite resent successfully",
      defaultError: "Error re-sending invite",
    },
    notifySuccess: true,
    notifyError: true,
    onSuccess: onNeedsRefresh, // refresh the user list on success
  });

  const [removeUserConfirmOpen, setRemoveUserConfirmOpen] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);

  if (deleteRequest.running) {
    return <InlineLoading />;
  }

  let userName = getUserName(user);

  const handleResendClick = () => {
    resendInvite();
    setResendDisabled(true);
    setTimeout(() => setResendDisabled(false), 5000);
  };

  if (self.email !== user.email) {
    // If the user is invited (but has not yet accepted), show "Resend Invite" + "Cancel Invite"
    if (user.status === "INVITED") {
      return (
        <>
          <Button variant="text" disabled={resendDisabled} onClick={() => handleResendClick()}>
            Resend Invite
          </Button>
          <Button color="error" variant="text" onClick={() => setRemoveUserConfirmOpen(true)}>
            Cancel Invite
          </Button>
          <ConfirmDialog
            open={removeUserConfirmOpen}
            onClose={() => setRemoveUserConfirmOpen(false)}
            onConfirm={deleteUser}
            confirmTitle={`Cancel ${userName}'s invite?`}
            contentText={`Are you sure you want to cancel ${userName}'s invite? Once you do, they won't have access to Polaris anymore.`}
            confirmText={`Cancel Invite`}
            confirmLoading={deleteRequest.running}
          />
        </>
      );
    }

    // Otherwise, for active users, show "Remove user"
    return (
      <>
        <Button color="error" variant="text" onClick={() => setRemoveUserConfirmOpen(true)}>
          Remove user
        </Button>
        <ConfirmDialog
          open={removeUserConfirmOpen}
          onClose={() => setRemoveUserConfirmOpen(false)}
          onConfirm={deleteUser}
          confirmTitle={`Remove ${userName}?`}
          contentText={`Are you sure you want to remove ${userName}? Once you do, they won't have access to Polaris anymore.`}
          confirmText={`Remove User`}
          confirmLoading={deleteRequest.running}
        />
      </>
    );
  }

  return null;
};

export const OrganizationUsersTable = ({
  loadingElem,
  errorElemFn,
  EmptyElem,
  allowAddingHrUser = true,
}: {
  loadingElem: React.ReactElement;
  errorElemFn: (errorMessage: string) => React.ReactElement;
  EmptyElem?: React.ReactElement;
  allowAddingHrUser?: boolean;
}) => {
  const primaryOrgId = usePrimaryOrganizationId();
  const [org] = usePrimaryOrganization();
  const [users, usersRequest, refreshUsers] = useOrganizationUsers(primaryOrgId, true);
  const currentOrgUser = useSelfOrganizationUser();
  const [self] = useSelf();

  const [inviteDialog, setInviteDialog] = useState(false);

  if (usersRequest.running) {
    return loadingElem;
  } else if (!users || usersRequest.error) {
    return errorElemFn(usersRequest.errorMessage || RequestsError.GetOrgUsers);
  }

  const activeUsers = users.filter((u) => u.status !== "INACTIVE");
  const currentOrgUsers = activeUsers.filter((u) => u.organizationName === org?.name);
  const parentOrgUsers = activeUsers.filter(
    (u) => u.organizationName !== org?.name && u.organization !== "TRUEVAULT",
  );
  const sortedActiveUsers = currentOrgUsers.sort((a, b) => {
    if (a.status === "INVITED" && b.status !== "INVITED") {
      return -1;
    } else if (a.status !== "INVITED" && b.status === "INVITED") {
      return 1;
    } else {
      const aName = `${a.firstName} ${a.lastName} ${a.email}`.trim();
      const bName = `${b.firstName} ${b.lastName} ${b.email}`.trim();
      return aName.localeCompare(bName);
    }
  });

  const existingUserEmails = new Set([
    ...currentOrgUsers.map((u) => u.email.toLowerCase()),
    ...parentOrgUsers.map((u) => u.email.toLowerCase()),
  ]);

  return (
    <div className="user-settings">
      {(currentOrgUser?.role === "ADMIN" || self?.globalRole === "ADMIN") && (
        <>
          <Button
            variant="contained"
            color="primary"
            className="mb-sm add-user"
            onClick={() => setInviteDialog(true)}
          >
            <AddIcon /> Add User
          </Button>
          <InviteUserDialog
            open={inviteDialog}
            organizationId={primaryOrgId}
            onClose={() => setInviteDialog(false)}
            onDone={() => {
              setInviteDialog(false);
              refreshUsers();
            }}
            allowAddingHrUser={allowAddingHrUser}
            existingUserEmails={existingUserEmails}
          />
        </>
      )}
      {users?.length ? (
        <UsersTable
          users={sortedActiveUsers}
          parentOrgUsers={parentOrgUsers}
          onNeedsRefresh={refreshUsers}
          allowAddingHrUser={allowAddingHrUser}
          org={org}
        />
      ) : (
        EmptyElem
      )}
    </div>
  );
};
